'use client';

import * as React from 'react';
import { createPortal } from 'react-dom';
import { cx } from '@/lib/ui';

export type ToastTone = 'neutral' | 'info' | 'success' | 'warning' | 'danger';

export type ToastOptions = {
  title: React.ReactNode;
  description?: React.ReactNode;
  tone?: ToastTone;
  durationMs?: number;
};

type ToastRecord = ToastOptions & { id: number };

type ToastContextValue = {
  toast: (options: ToastOptions) => number;
  dismiss: (id: number) => void;
};

const ToastContext = React.createContext<ToastContextValue | null>(null);

const toneClasses: Record<ToastTone, string> = {
  neutral: 'border-slate-200 bg-white text-slate-900',
  info: 'border-sky-200 bg-sky-50 text-sky-950',
  success: 'border-emerald-200 bg-emerald-50 text-emerald-950',
  warning: 'border-amber-200 bg-amber-50 text-amber-950',
  danger: 'border-rose-200 bg-rose-50 text-rose-950',
};

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [items, setItems] = React.useState<ToastRecord[]>([]);
  const [mounted, setMounted] = React.useState(false);
  const nextId = React.useRef(1);
  const timers = React.useRef(new Map<number, number>());

  React.useEffect(() => setMounted(true), []);

  const dismiss = React.useCallback((id: number) => {
    const timer = timers.current.get(id);
    if (timer) window.clearTimeout(timer);
    timers.current.delete(id);
    setItems((current) => current.filter((item) => item.id !== id));
  }, []);

  const toast = React.useCallback(
    (options: ToastOptions) => {
      const id = nextId.current++;
      setItems((current) => [...current, { id, ...options }]);

      const durationMs = options.durationMs ?? 4500;
      if (durationMs > 0) {
        const timer = window.setTimeout(() => dismiss(id), durationMs);
        timers.current.set(id, timer);
      }

      return id;
    },
    [dismiss]
  );

  React.useEffect(
    () => () => {
      for (const timer of timers.current.values()) window.clearTimeout(timer);
      timers.current.clear();
    },
    []
  );

  const value = React.useMemo(() => ({ toast, dismiss }), [toast, dismiss]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      {mounted && items.length
        ? createPortal(
            <div
              className="pointer-events-none fixed inset-x-0 top-4 z-[1100] mx-auto flex w-full max-w-md flex-col gap-2 px-4 sm:left-auto sm:right-4 sm:mx-0 sm:px-0"
              aria-live="polite"
              aria-relevant="additions"
            >
              {items.map((item) => {
                const tone = item.tone ?? 'neutral';
                return (
                  <div
                    key={item.id}
                    role={tone === 'danger' ? 'alert' : 'status'}
                    className={cx(
                      'pointer-events-auto rounded-aula-lg border p-4 shadow-aula-floating',
                      toneClasses[tone]
                    )}
                  >
                    <div className="flex items-start gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-bold">{item.title}</div>
                        {item.description ? (
                          <div className="mt-1 text-sm leading-5 opacity-80">
                            {item.description}
                          </div>
                        ) : null}
                      </div>
                      <button
                        type="button"
                        onClick={() => dismiss(item.id)}
                        className="rounded-aula-sm px-1.5 py-0.5 text-lg leading-none opacity-60 transition hover:bg-black/5 hover:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-aula-primary/35"
                        aria-label="Dismiss notification"
                      >
                        ×
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>,
            document.body
          )
        : null}
    </ToastContext.Provider>
  );
}

export function useToast() {
  const value = React.useContext(ToastContext);
  if (!value) {
    throw new Error('useToast must be used inside ToastProvider');
  }
  return value;
}
